import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { siteConfig } from "../data/mock";
import { Menu, X, Camera, Instagram, ArrowUp } from "lucide-react";
import { Button } from "./ui/button";

const navLinks = [
  { label: "Home", path: "/" },
  { label: "Portfolio", path: "/portfolio" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        isScrolled
          ? "bg-[#0A0A0A]/95 backdrop-blur-md border-b border-[#262626]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <Camera className="w-7 h-7 text-amber-500 transition-transform duration-300 group-hover:rotate-12" />
            <div>
              <span className="font-bebas text-xl tracking-wider text-white">
                Killed By Muppets
              </span>
              <span className="hidden sm:block text-[10px] uppercase tracking-[0.3em] text-amber-500/80 -mt-1">
                Photography
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-200 rounded-md ${
                  location.pathname === link.path
                    ? "text-amber-500"
                    : "text-neutral-400 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/contact">
              <Button className="ml-4 bg-amber-500 hover:bg-amber-600 text-black font-semibold text-sm px-5 rounded-md">
                Book a Shoot
              </Button>
            </Link>
          </nav>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ${
          mobileOpen ? "max-h-80 opacity-100" : "max-h-0 opacity-0"
        } bg-[#0A0A0A]/98 backdrop-blur-lg border-b border-[#262626]`}
      >
        <nav className="flex flex-col px-6 py-4 gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`py-3 text-base font-medium tracking-wide transition-colors duration-200 ${
                location.pathname === link.path
                  ? "text-amber-500"
                  : "text-neutral-400 hover:text-white"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link to="/contact" className="mt-2">
            <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold">
              Book a Shoot
            </Button>
          </Link>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 600);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <footer className="bg-[#080808] border-t border-[#1a1a1a]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Camera className="w-6 h-6 text-amber-500" />
              <span className="font-bebas text-xl tracking-wider text-white">
                Killed By Muppets
              </span>
            </div>
            <p className="text-neutral-500 text-sm leading-relaxed max-w-xs">
              Professional sports & event photography based in Derby, UK. Capturing the moments that define the game.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bebas text-lg tracking-wider text-white mb-4">Quick Links</h4>
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-neutral-500 hover:text-amber-500 text-sm transition-colors duration-200"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="font-bebas text-lg tracking-wider text-white mb-4">Get In Touch</h4>
            <p className="text-neutral-500 text-sm mb-1">{siteConfig.email}</p>
            <p className="text-neutral-500 text-sm mb-4">{siteConfig.phone}</p>
            <div className="flex gap-4">
              <a
                href={siteConfig.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-500 hover:text-amber-500 transition-colors duration-200"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-[#1a1a1a] mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-neutral-600 text-xs">
            &copy; {new Date().getFullYear()} Killed By Muppets Photography. All rights reserved.
          </p>
          <p className="text-neutral-600 text-xs">
            Derby, UK
          </p>
        </div>
      </div>

      {/* Scroll to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`fixed bottom-8 right-8 bg-amber-500 hover:bg-amber-600 text-black p-3 rounded-full shadow-lg transition-all duration-300 z-40 ${
          showScrollTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        }`}
        aria-label="Scroll to top"
      >
        <ArrowUp className="w-5 h-5" />
      </button>
    </footer>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
